import './styles.sass';

import { map, uniq } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Transition from 'components/Explorer/Transition';
import Spinner from 'components/Explorer/Spinner';
import * as QueryParams from 'components/Explorer/models/query_params';
import * as QueryFetcher from 'components/Explorer/models/query_fetcher';

import Post from '../Mentions/Post';
import useQueryParams from 'hooks/useQueryParams';

const RecentMentions = ({ visible, mentionSource, mentionsLink, filtersForMentionSource }) => {
  const params = useQueryParams();

  const [loading, setLoading] = useState(false);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [data, setData] = useState([]);

  const inFlightRef = useRef(null);
  // Tracks the filtersKey we last successfully loaded for, so the effect
  // refetches when the URL filters change while the panel stays mounted.
  const loadedKeyRef = useRef(null);
  const filtersKey = JSON.stringify({ params: params.filters, source: filtersForMentionSource });

  useEffect(() => {
    if (!visible) return;
    if (loadedKeyRef.current === filtersKey) return;

    setLoading(true);
    setLoadingFailed(false);

    inFlightRef.current?.abort();

    const scopedParams = QueryParams.withPerPage(QueryParams.withFilters(params, filtersForMentionSource), 3);
    const request = QueryFetcher.fetchJSON('mentions', scopedParams);
    inFlightRef.current = request;

    request.then((err, response) => {
      inFlightRef.current = null;
      if (err?.name === 'AbortError') return;

      const failed = err || typeof response?.body === 'undefined';
      setLoading(false);
      setLoadingFailed(failed);
      if (!failed) {
        setData(response.body.data);
        loadedKeyRef.current = filtersKey;
      } else {
        setData(null);
      }
    });

    return () => request.abort();
  }, [visible, filtersKey]);

  if (!visible) return null;

  return (
    <div className="MentionSources-RecentMentions">
      <Transition>
        {loading ? (
          <Spinner key="spinner" />
        ) : loadingFailed ? (
          <div key="error-message">{I18n.t('Explorer.loading_error')}</div>
        ) : (
          <div key="content">
            <div className="mention-timeline">
              {data.map(([date, posts]) => {
                const uniqPosts = uniq(posts);
                return (
                  <div className="mention-date-boundary" key={`mentions-${date}`}>
                    {map(uniqPosts, (post, index) => (
                      <Post first={index === 0} post={post} key={post.id} />
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="MentionSources-RecentMentions-All">
              <Link className="view-all-button" to={mentionsLink}>
                {I18n.t('Explorer.MentionSources.mentions_button', { count: mentionSource.mentions })}
              </Link>
            </div>
          </div>
        )}
      </Transition>
    </div>
  );
};

RecentMentions.propTypes = {
  visible: PropTypes.bool.isRequired,
  mentionSource: PropTypes.object.isRequired,
  mentionsLink: PropTypes.object.isRequired,
  filtersForMentionSource: PropTypes.object.isRequired
};

export default RecentMentions;
